import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { investigationService } from '../services/api';
import Graph from '../components/Graph';
import '../styles/FocusedGraphView.css';

export default function FocusedGraphView() {
  const { id }    = useParams();
  const { token } = useAuthStore();

  const [investigation, setInvestigation] = useState(null);
  const [focusedData,   setFocusedData]   = useState(null); // pre-filtered graph data
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  // Node/edge selection — still available so clicking a node/edge in focused view
  // shows the tooltip/label, even though there's no sidebar card here.
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [txFilter,     setTxFilter]     = useState('all');

  // Reset txFilter when selected node changes (mirrors InvestigationDetail behaviour)
  useEffect(() => { setTxFilter('all'); }, [selectedNode]);

  const svgExportRef = useRef(null); // Graph exposes its SVG export fn via this ref callback

  const load = useCallback(async () => {
    if (!token) { setError('Not authenticated'); setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);

      const [invRes, graphRes, viewStateRes] = await Promise.all([
        investigationService.get(id),
        investigationService.getGraph(id),
        investigationService.getGraphViewState(id),
      ]);

      const inv      = invRes.data;
      const graph    = graphRes.data || { nodes: [], edges: [] };
      const vs       = viewStateRes.data || {};
      const hiddenN  = new Set(vs.hidden_nodes || []);
      const hiddenE  = new Set(vs.hidden_edges || []);

      // Pre-filter: remove hidden nodes and edges entirely so the cy instance
      // never sees them — no restore controls needed in this view.
      const filteredNodes = (graph.nodes || []).filter(n => {
        const id = n.id || n.address || '';
        return id && !hiddenN.has(id);
      });
      const visibleNodeIds = new Set(filteredNodes.map(n => n.id || n.address || ''));
      const filteredEdges = (graph.edges || []).filter((e, i) => {
        const eid = e.id || `e${i}`;
        if (hiddenE.has(eid)) return false;
        // Also drop edges whose source or target node is hidden
        const src = e.from || e.source || '';
        const tgt = e.to   || e.target || '';
        return visibleNodeIds.has(src) && visibleNodeIds.has(tgt);
      });

      setInvestigation(inv);
      setFocusedData({ nodes: filteredNodes, edges: filteredEdges });
    } catch (err) {
      console.error('FocusedGraphView load error:', err);
      setError('Failed to load investigation graph.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  // Export PNG — calls the Graph's internal export (same as main view)
  const handleExportPng = useCallback(() => {
    // Graph.js handleExport is internal; we trigger it by dispatching a custom event
    // that Graph listens for, OR we simply expose a ref. The simplest approach here:
    // re-use the existing toolbar Export PNG button that Graph renders internally —
    // it's already in the toolbar, so no extra wiring needed. This button is a bonus
    // for when the investigator wants PNG from the focused view toolbar specifically.
    // Implementation: expose export via a window-level helper set by Graph on mount.
    if (window.__focusedGraphExportPng) window.__focusedGraphExportPng();
  }, []);

  const handleExportSvg = useCallback(() => {
    if (window.__focusedGraphExportSvg) window.__focusedGraphExportSvg();
  }, []);

  if (loading) return <div className="focused-loading">Loading focused graph…</div>;
  if (error)   return <div className="focused-error">⚠ {error}</div>;
  if (!focusedData?.nodes?.length) return (
    <div className="focused-error">No visible nodes — restore some items in the main investigation view first.</div>
  );

  return (
    <div className="focused-view">
      <div className="focused-topbar">
        <div className="focused-topbar-title">
          <h1>{investigation.title}</h1>
          <span className="focused-badge">🔍 Focused View</span>
        </div>

        <div className="focused-stats">
          <div className="focused-stat">
            <strong>{focusedData.nodes.length}</strong>
            <label>Addresses</label>
          </div>
          <div className="focused-stat">
            <strong>{focusedData.edges.length}</strong>
            <label>Transactions</label>
          </div>
        </div>

        <div className="focused-topbar-sep" />

        <button className="focused-btn" onClick={handleExportPng}>📥 Export PNG</button>
        <button className="focused-btn focused-btn-svg" onClick={handleExportSvg}>📐 Export SVG</button>
      </div>

      <div className="focused-graph-area">
        <Graph
          data={focusedData}
          startAddress={investigation.start_address}
          hiddenEdges={new Set()}
          hiddenNodes={new Set()}
          onViewStateChange={null}
          onNodeSelect={setSelectedNode}
          onEdgeSelect={setSelectedEdge}
          txFilter={txFilter}
          onTxFilterChange={setTxFilter}
          isFocusedView={true}
        />
      </div>
    </div>
  );
}
